import axios from 'axios';

export interface Message {
  id: string;
  type: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  thinkingStartTime?: Date;
  thinkingDuration?: number; // in seconds
  csvMetadata?: {
    full_csv_download: string;
    full_csv_row_count: number;
  };
}

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';

// Configure axios
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 300000, // 5 minutes timeout for AI responses (complex queries can take time)
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Include cookies for session authentication
});

// Add request interceptor for logging
apiClient.interceptors.request.use(
  (config) => {
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor for error handling
apiClient.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    
    if (error.response?.status === 404) {
      throw new Error('API endpoint not found. Make sure the backend server is running.');
    } else if (error.response?.status >= 500) {
      throw new Error('Server error. Please try again later.');
    } else if (error.code === 'ECONNREFUSED' || error.code === 'ERR_NETWORK') {
      throw new Error('Cannot connect to server. Please check if the backend is running on ' + API_BASE_URL);
    } else {
      throw new Error(error.response?.data?.detail || error.message || 'An unexpected error occurred');
    }
  }
);

export const sendMessage = async (message: string, conversationId: string): Promise<string> => {
  try {
    const response = await apiClient.post('/chat', {
      message,
      conversation_id: conversationId,
    });

    return response.data.response || response.data.message || 'No response received';
  } catch (error) {
    throw error;
  }
};

// New streaming chat service
export const sendMessageStream = async (
  message: string,
  conversationId: string,
  onMessage: (content: string) => void,
  title?: string,
  onStatus?: (status: string) => void,
): Promise<{conversationId: string, responseId: string | null, csvMetadata?: {full_csv_download: string, full_csv_row_count: number}, smartTitle?: string}> => {
  const url = `${API_BASE_URL}/chat/stream`;

  try {
    const requestBody: any = {
      message,
      conversation_id: conversationId,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone, // Get browser timezone
    };

    // Include title if provided (for new conversations)
    if (title) {
      requestBody.title = title;
    }

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include', // Include cookies for session authentication
      body: JSON.stringify(requestBody),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const reader = response.body?.getReader();
    if (!reader) {
      throw new Error('No response body reader available');
    }

    const decoder = new TextDecoder();
    let buffer = '';
    let finalConversationId = conversationId;
    let finalResponseId: string | null = null;
    let csvMetadata: {full_csv_download: string, full_csv_row_count: number} | undefined = undefined;
    let smartTitle: string | undefined = undefined;

    while (true) {
      const { done, value } = await reader.read();

      if (done) break;

      buffer += decoder.decode(value, { stream: true });

      // Process complete lines
      const lines = buffer.split('\n');
      buffer = lines.pop() || ''; // Keep the incomplete line in buffer

      for (const line of lines) {
        if (line.startsWith('data: ')) {
          try {
            const data = JSON.parse(line.slice(6));

            if (data.type === 'status') {
              if (onStatus && data.message) {
                onStatus(data.message);
              }
            } else if (data.type === 'content') {
              onMessage(data.message);
            } else if (data.type === 'done') {
              // Capture the OpenAI conversation ID and response ID
              if (data.conversation_id) {
                finalConversationId = data.conversation_id;
              }
              if (data.response_id) {
                finalResponseId = data.response_id;
              }
              // Capture CSV metadata if present
              if (data.csv_metadata) {
                csvMetadata = data.csv_metadata;
              }
              // Capture smart title if present
              if (data.smart_title) {
                smartTitle = data.smart_title;
              }
            } else if (data.type === 'error') {
              throw new Error(data.message);
            }
          } catch (parseError) {
            // Ignore parse errors
          }
        }
      }
    }

    return { conversationId: finalConversationId, responseId: finalResponseId, csvMetadata, smartTitle };
  } catch (error) {
    throw error;
  }
};

export const getHealth = async (): Promise<boolean> => {
  try {
    const response = await apiClient.get('/health');
    return response.status === 200;
  } catch (error) {
    return false;
  }
};

export const renameConversation = async (conversationId: string, newTitle: string): Promise<void> => {
  try {
    await apiClient.put(`/api/conversations/${conversationId}/rename`, {
      title: newTitle
    });
  } catch (error) {
    throw error;
  }
};

export const deleteConversation = async (conversationId: string): Promise<void> => {
  try {
    await apiClient.delete(`/api/conversations/${conversationId}`);
  } catch (error) {
    throw error;
  }
};

// ========== Favorite Prompts ==========
// Stored server-side (PlateIQ ``CopilotUserFavoritePrompt``) and proxied through
// this Flask app, so the session cookie is the only credential the browser holds.
// Every call opts out of the shared error interceptor with validateStatus so the
// backend's own message survives — the interceptor only forwards `detail`.

export interface FavoritePrompt {
  id: string;
  name: string;
  prompt_text: string;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

const FAVORITE_PROMPTS_URL = '/api/favorite-prompts';

const anyStatus = { validateStatus: () => true };

const favoritePromptError = (response: any, fallback: string): Error =>
  new Error(response.data?.error || response.data?.detail || fallback);

export const listFavoritePrompts = async (): Promise<FavoritePrompt[]> => {
  const response = await apiClient.get(FAVORITE_PROMPTS_URL, anyStatus);

  // A user without a Copilot plan simply has no favorites surface; that is not
  // an error worth showing, so the sidebar section stays empty and silent.
  if (response.status === 401 || response.status === 403) {
    return [];
  }
  if (response.status !== 200) {
    throw favoritePromptError(response, 'Unable to load your favorite prompts.');
  }

  return response.data?.favorite_prompts || [];
};

export const createFavoritePrompt = async (
  promptText: string,
  name?: string,
): Promise<FavoritePrompt> => {
  const response = await apiClient.post(
    FAVORITE_PROMPTS_URL,
    { prompt_text: promptText, ...(name ? { name } : {}) },
    anyStatus,
  );

  if (response.status !== 201) {
    // Carries the 25-prompt cap message and the 10,000-character bound.
    throw favoritePromptError(response, 'Unable to save this prompt.');
  }

  return response.data.favorite_prompt;
};

export const deleteFavoritePrompt = async (promptId: string): Promise<void> => {
  const response = await apiClient.delete(`${FAVORITE_PROMPTS_URL}/${promptId}`, anyStatus);

  if (response.status !== 200) {
    throw favoritePromptError(response, 'Unable to delete this favorite prompt.');
  }
};
